import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Check,
  ChevronDown,
} from "lucide-react";
import {
  differenceInCalendarDays,
  startOfDay,
} from "date-fns";

import {
  formatRange,
  inclusiveDays,
  presetRange,
} from "@/components/overview/dateRangeModel";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const PRESETS = [
  { days: 7, label: "Last 7 days" },
  { days: 14, label: "Last 14 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
];

function useDesktopCalendar() {
  const [isDesktop, setIsDesktop] = useState(() => (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(min-width: 900px)").matches
  ));

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(min-width: 900px)");
    const update = () => setIsDesktop(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);

  return isDesktop;
}

export default function OverviewDateRangePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [month, setMonth] = useState(value?.from);
  const isDesktop = useDesktopCalendar();
  const days = inclusiveDays(value);
  const draftDays = inclusiveDays(draft);

  useEffect(() => {
    if (!open) {
      setDraft(value);
      setMonth(value?.from);
    }
  }, [open, value]);

  const activePreset = useMemo(() => {
    const today = startOfDay(new Date());
    if (!draft?.from || !draft?.to || differenceInCalendarDays(draft.to, today) !== 0) {
      return null;
    }
    return PRESETS.find((preset) => preset.days === draftDays)?.days || null;
  }, [draft, draftDays]);

  const apply = () => {
    if (!draft?.from || !draft?.to) return;
    onChange({
      from: startOfDay(draft.from),
      to: startOfDay(draft.to),
    });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid="overview-date-range"
          variant="outline"
          className="h-11 min-w-[248px] justify-start rounded-xl border-[#0E5B52]/15 bg-white px-3.5 text-left shadow-sm hover:bg-[#F7FBF8] hover:border-[#0E5B52]/25"
          aria-label={`Date range: ${formatRange(value)}`}
        >
          <span className="w-8 h-8 rounded-lg bg-[#E6F5EE] flex items-center justify-center mr-3 flex-shrink-0">
            <CalendarDays className="w-4 h-4 text-[#0E5B52]" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[13px] leading-tight font-semibold text-[#0F2A26] truncate">
              {formatRange(value)}
            </span>
            <span className="block text-[10px] leading-tight text-[#6B7280] mt-0.5">
              {days} calendar {days === 1 ? "day" : "days"}
            </span>
          </span>
          <ChevronDown className="w-4 h-4 ml-3 text-[#0E5B52]/50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-auto max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border-[#0E5B52]/12 bg-white p-0 shadow-[0_24px_70px_rgba(6,78,59,0.18)]"
      >
        <div className="flex">
          <div className="hidden sm:block w-40 flex-shrink-0 border-r border-[#0E5B52]/10 bg-[#F7FBF8] p-3">
            <div className="px-2 pb-2 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#0E5B52]/60">
              Quick ranges
            </div>
            <div className="space-y-1">
              {PRESETS.map((preset) => (
                <button
                  key={preset.days}
                  type="button"
                  onClick={() => {
                    const next = presetRange(preset.days);
                    setDraft(next);
                    setMonth(next.from);
                  }}
                  className={cn(
                    "w-full flex items-center justify-between rounded-lg px-2.5 py-2 text-left text-xs font-medium transition-colors",
                    activePreset === preset.days
                      ? "bg-[#0E5B52] text-white"
                      : "text-[#36534E] hover:bg-[#E6F5EE] hover:text-[#064E3B]",
                  )}
                >
                  {preset.label}
                  {activePreset === preset.days && <Check className="w-3.5 h-3.5" />}
                </button>
              ))}
            </div>
          </div>

          <div className="min-w-0">
            <div className="px-4 pt-4 pb-1">
              <div className="text-sm font-semibold text-[#0F2A26]">Select reporting period</div>
              <div className="text-[11px] text-[#6B7280] mt-0.5">Up to 90 days · future dates unavailable</div>
            </div>
            <Calendar
              initialFocus
              mode="range"
              month={month}
              onMonthChange={setMonth}
              selected={draft}
              onSelect={(next) => setDraft(next || draft)}
              numberOfMonths={isDesktop ? 2 : 1}
              disabled={{ after: new Date() }}
              className="p-4 pt-2"
              classNames={{
                months: "flex flex-col md:flex-row gap-4",
                month: "space-y-3",
                day_selected: "bg-[#0E5B52] text-white hover:bg-[#0A4640] hover:text-white focus:bg-[#0E5B52] focus:text-white",
                day_range_middle: "aria-selected:bg-[#DDF4E8] aria-selected:text-[#064E3B]",
                day_today: "border border-[#0E5B52]/30 text-[#064E3B]",
              }}
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-[#0E5B52]/10 bg-[#FBFDFC] px-4 py-3">
          <div className="text-xs text-[#4B5563]">
            {draft?.from && draft?.to ? (
              <>
                <span className="font-semibold text-[#064E3B]">{formatRange(draft)}</span>
                <span className="ml-2 text-[#9CA3AF]">{draftDays} days</span>
              </>
            ) : "Select a start and end date"}
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              className="h-8 rounded-lg text-xs"
              onClick={() => {
                setDraft(value);
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="h-8 rounded-lg bg-[#0E5B52] px-4 text-xs hover:bg-[#0A4640]"
              disabled={!draft?.from || !draft?.to || draftDays > 90}
              onClick={apply}
            >
              Apply range
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
